import React, { useEffect, useState } from 'react'
import Modal from './Modal'
import Button from '../ui/buttons/Button'
import { X, ShieldCheck } from 'lucide-react'
import PasswordInput from '../ui/inputs/PasswordInput'
import { fetchWithCsrf } from '../../utils/fetchWithCsrf'
import { camelizeKeys } from '../../utils/camelizeKeys'
import CustomButton from '../ui/buttons/CustomButton'
import SecondaryButton from '../ui/buttons/SecondaryButton'
import TwoFactorStatusBadge from '../ui/badges/TwoFactorStatusBadge'
import { useToast } from '../ui/toast/ToastContext'

const QrCodeDisplay = React.memo(({ svg }) => (
  <div className="bg-white p-4" dangerouslySetInnerHTML={{ __html: svg }} />
))

const OtpInput = React.memo(({ value, onChange }) => (
  <div className="md:col-span-2">
    <label htmlFor="otpAttempt" className="text-lg text-blueDarker font-normal block mb-1">
      Código de verificação (6 dígitos)
    </label>
    <input
      id="otpAttempt"
      name="otpAttempt"
      type="text"
      inputMode="numeric"
      maxLength="6"
      value={value}
      onChange={onChange}
      className="w-full border border-bluePale rounded-xl px-3 py-3 text-base placeholder-blueSteel"
    />
  </div>
))

const SecuritySettingsModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
    otpEnabled: false,
    has2FAConfigured: false,
    qrCodeSvg: ''
  })
  const [otpAttempt, setOtpAttempt] = useState('')
  const [otpStage, setOtpStage] = useState('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const { addToast } = useToast()

  useEffect(() => {
    fetch('/app/planner/security/security_status')
      .then(res => res.json())
      .then(data => {
        const camel = camelizeKeys(data)
        setForm(prev => ({
          ...prev,
          otpEnabled: camel.otpRequiredForLogin,
          has2FAConfigured: camel.otpSecretExists,
          qrCodeSvg: camel.qrCodeSvg || ''
        }))
      })
      .catch(err => {
        console.error('Erro ao carregar configurações de segurança', err)
        addToast({ type: 'error', message: 'Erro ao carregar configurações de segurança.' })
      })
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrorMessage('')

    const payload = {
      new_password: form.newPassword,
      confirm_password: form.confirmPassword
    }

    fetchWithCsrf('/app/planner/security/update_security', {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
      .then(async (res) => {
        if (!res.ok) {
          const json = await res.json()
          throw new Error(json.errors?.join(', ') || 'Erro ao atualizar senha.')
        }

        addToast({ type: 'success', message: 'Senha alterada com sucesso!' })
        window.location.reload()
      })
      .catch(err => {
        console.error('Erro ao alterar senha:', err)
        setErrorMessage(err.message)
        addToast({ type: 'error', message: err.message })
      })
  }

  const handleEnable2FA = () => {
    setErrorMessage('')
    fetchWithCsrf('/app/planner/security/generate_otp_secret', { method: 'POST' })
      .then(res => res.json())
      .then(data => {
        const camel = camelizeKeys(data)
        setForm(prev => ({ ...prev, qrCodeSvg: camel.qrCodeSvg }))
        setOtpStage('awaiting_otp')
      })
      .catch(err => {
        console.error('Erro ao ativar 2FA:', err)
        addToast({ type: 'error', message: 'Erro ao ativar 2FA.' })
      })
  }

  const handleVerifyOTP = () => {
    setErrorMessage('')
    fetchWithCsrf('/app/planner/security/verify_otp', {
      method: 'POST',
      body: JSON.stringify({ otp_attempt: otpAttempt })
    })
      .then(res => {
        if (!res.ok) throw new Error('Código inválido')
        setOtpStage('idle')
        addToast({ type: 'success', message: '2FA ativado com sucesso!' })
        onSuccess()
      })
      .catch(err => {
        setErrorMessage(err.message)
        addToast({ type: 'error', message: err.message })
      })
  }

  const handleDisable2FA = () => {
    setErrorMessage('')
    fetchWithCsrf('/app/planner/security/disable_2fa', { method: 'POST' })
      .then(() => {
        setForm(prev => ({
          ...prev,
          has2FAConfigured: false,
          otpEnabled: false,
          qrCodeSvg: ''
        }))
        setOtpAttempt('')
        setOtpStage('idle')
        addToast({ type: 'success', message: '2FA desativado com sucesso.' })
        onSuccess()
      })
      .catch(err => {
        console.error('Erro ao desativar 2FA:', err)
        addToast({ type: 'error', message: 'Erro ao desativar 2FA.' })
      })
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <ShieldCheck size={24} className="text-tealMedium" strokeWidth={1.25} />
            Ajustes de Segurança
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <div className="md:col-span-2">
            <h3 className="text-lg text-blueDarker font-semibold mb-2">Alterar senha</h3>
          </div>

          <div className="md:col-span-2 flex items-end justify-between gap-4">
            <div className="flex-1">
              <PasswordInput name="newPassword" label="Nova senha" value={form.newPassword} onChange={handleChange} required />
            </div>
            <div className="flex-1">
              <PasswordInput name="confirmPassword" label="Confirmar nova senha" value={form.confirmPassword} onChange={handleChange} required />
            </div>
            <div className="h-full flex items-end">
              <CustomButton type="submit" className="w-44 h-custom50">ALTERAR SENHA</CustomButton>
            </div>
          </div>

          <div className="md:col-span-2 border-t border-bluePale my-4" />

          <div className="md:col-span-2 flex items-center">
            <h3 className="text-lg text-blueDarker font-semibold mr-5">Autenticação de dois fatores (2FA)</h3>
            <TwoFactorStatusBadge enabled={form.otpEnabled} />
          </div>

          <div className="md:col-span-2 text-sm text-blueSteel mb-2">
            O 2FA adiciona uma camada extra de segurança à sua conta exigindo um código do seu app autenticador (Google Authenticator, Authy...), além da senha.
          </div>

          {otpStage === 'awaiting_otp' && form.qrCodeSvg && (
            <>
              <div className="md:col-span-2 flex items-center justify-center text-center flex-col">
                <p className="text-blueDark text-base font-medium mb-2">Escaneie este QR Code com seu app autenticador</p>
                <QrCodeDisplay svg={form.qrCodeSvg} />
              </div>
              <div className="md:col-span-2 flex items-end justify-between gap-4">
                <div className="flex-1">
                  <OtpInput value={otpAttempt} onChange={e => setOtpAttempt(e.target.value)} />
                </div>
                <div className="h-full flex items-end">
                  <CustomButton type="button" onClick={handleVerifyOTP} className="h-custom50">
                    VERIFICAR CÓDIGO
                  </CustomButton>
                </div>
              </div>
            </>
          )}

          {otpStage === 'idle' && !form.otpEnabled && (
            <CustomButton type="button" onClick={handleEnable2FA} className="w-44">
              ATIVAR 2FA
            </CustomButton>
          )}

          {otpStage === 'idle' && form.otpEnabled && (
            <div className="md:col-span-2 flex gap-5 items-center">
              <CustomButton className="w-44" type="button" onClick={() => setOtpStage('confirm_reconfigure')}>
                CONFIGURAR 2FA
              </CustomButton>
              <SecondaryButton className="w-44" type="button" onClick={() => setOtpStage('confirm_disable')}>
                DESATIVAR 2FA
              </SecondaryButton>
            </div>
          )}

          {otpStage === 'confirm_reconfigure' && (
            <div className="md:col-span-2 border border-yellow-300 bg-yellow-50 p-4 rounded-lg">
              <p className="text-blueDarker text-base mb-4">
                Isso irá reconfigurar sua autenticação 2FA. Você precisará escanear um novo QR Code e inserir um novo código. Deseja continuar?
              </p>
              <div className="flex justify-end gap-3">
                <CustomButton className="w-32 h-custom50" type="button" onClick={() => setOtpStage('idle')} variant="secondary">
                  CANCELAR
                </CustomButton>
                <CustomButton className="w-32 h-custom50" type="button" onClick={handleEnable2FA}>
                  CONFIRMAR
                </CustomButton>
              </div>
            </div>
          )}

          {otpStage === 'confirm_disable' && (
            <div className="md:col-span-2 border border-red-300 bg-red-50 p-4 rounded-lg">
              <p className="text-blueDarker text-base mb-4">
                Você está prestes a desativar a autenticação 2FA. Isso reduzirá a segurança da sua conta. Deseja continuar?
              </p>
              <div className="flex justify-end gap-3">
                <CustomButton className="w-32" type="button" onClick={() => setOtpStage('idle')}>
                  CANCELAR
                </CustomButton>
                <CustomButton type="button" onClick={handleDisable2FA} className="w-32">
                  CONFIRMAR
                </CustomButton>
              </div>
            </div>
          )}
        </form>
      </div>
    </Modal>
  )
}

export default SecuritySettingsModal
